/**
 * The three ways a group's drops use its lists, as cards (the Gateways
 * strategy cards' look — same radio mark, same hint dot). Each card draws
 * its pattern as a tiny grid — 4 drops across, 3 lists down — so the
 * difference reads at a glance: one row, every row, a staircase.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { HintDot } from '@/components/ui';
import { cn } from '@/lib/utils';

import { RadioMark } from '../../gateways/cards/StrategyCards';
import { type ListMode, MODE_CARDS } from './list-mode';
import { dropTargets } from './matrix-model';

const GLYPH_DROPS = Array.from({ length: 4 }, (_, i) => ({ label: String(i) }));

const MiniGrid: FC<{ mode: ListMode; on: boolean }> = ({ mode, on }) => {
  const targets = dropTargets(mode, GLYPH_DROPS, 3);
  return (
    <span className="grid grid-cols-4 gap-0.5" aria-hidden>
      {[0, 1, 2].flatMap((row) =>
        targets.map((hit, col) => (
          <span
            key={`${row}-${col}`}
            className={cn(
              'h-1.5 w-1.5 rounded-full transition-colors duration-300',
              hit.includes(row) ? (on ? 'bg-primary' : 'bg-foreground/55') : 'bg-muted-foreground/20',
            )}
          />
        )),
      )}
    </span>
  );
};

export const ModeCards: FC<{ mode: ListMode; onSelect: (mode: ListMode) => void }> = ({ mode, onSelect }) => (
  <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="How the drops use the lists">
    {MODE_CARDS.map((card) => {
      const on = mode === card.mode;
      return (
        <button
          key={card.mode}
          type="button"
          role="radio"
          aria-checked={on}
          aria-label={card.title}
          onClick={() => onSelect(card.mode)}
          className={cn(
            'flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-left transition-colors duration-200',
            on ? 'border-primary bg-primary/5 shadow-sm' : 'border-input bg-background hover:border-primary/50',
          )}
        >
          <RadioMark on={on} />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="flex items-center gap-1.5 text-xs font-semibold">
              {card.title}
              <HintDot label={card.hint} />
            </span>
            <span className="truncate text-[10.5px] text-muted-foreground">{card.caption}</span>
          </span>
          <MiniGrid mode={card.mode} on={on} />
        </button>
      );
    })}
  </div>
);
